/**
 * The network half of the identifier tools — browser-side, on a button press.
 *
 * Three sources, and nothing else:
 *
 * - **`/api/cite`**, CitationEasy's own lookup route. It resolves a PMID, PMCID or web
 *   address through PubMed or the page itself and returns the parsed record as `entry`.
 * - **Crossref's REST API** (`api.crossref.org/works`), which answers browsers
 *   directly. The retraction checker reads each work's `updated-by` notices from it —
 *   the publishers' own deposits and, since Crossref acquired it in 2023, the Retraction
 *   Watch database.
 * - **`/data/journal-abbreviations.txt`**, the static NLM journal list on this site.
 *
 * Lookups run one at a time: every one of them ends at a public service that
 * rate-limits bursts, and a reading list is not urgent enough to risk that. Crossref
 * lookups go twenty DOIs to a request instead (`createBatchedLookup`).
 */

import type { EditableEntry } from "@/lib/citation";
import {
  type JournalList,
  parseJournalAsset,
} from "@/lib/tool-kit/identifiers/journal-abbreviations";

/** The record `/api/cite` returns: CSL fields flattened, authors as one string. */
export type CiteEntry = EditableEntry;

export type LookupFailure =
  | "not-found"
  | "rate-limited"
  | "blocked"
  | "unavailable"
  | "timeout"
  | "network";

export type Lookup<T> =
  | { ok: true; value: T }
  | { ok: false; reason: LookupFailure };

const SOURCE_FAILURES = new Set<string>([
  "blocked",
  "not-found",
  "rate-limited",
  "unavailable",
  "timeout",
  "network",
]);

const RE_TIMED_OUT = /timed out|timeout/i;
/** "Server responded with status code 403", "Open Library returned 404". */
const RE_UPSTREAM_STATUS = /(?:status code|returned) (\d{3})\b/i;

interface CiteResponseBody {
  entry?: CiteEntry | null;
  error?: string;
  code?: string;
}

/** What an upstream HTTP status means for the reader. */
function failureForStatus(status: number): LookupFailure {
  if (status === 429) {
    return "rate-limited";
  }
  if (status === 401 || status === 402 || status === 403 || status === 451) {
    return "blocked";
  }
  if (status === 408 || status >= 500) {
    return "unavailable";
  }
  return "not-found";
}

/**
 * Why `/api/cite` failed. A page-fetch failure carries a `code`; a registry failure
 * only says so in its message, which names the upstream status — and a 403 from
 * PubMed is a refusal worth retrying, not a missing record.
 */
export function citeFailure(
  status: number,
  body: CiteResponseBody | null
): LookupFailure {
  if (status === 429) {
    return "rate-limited";
  }
  if (body?.code && SOURCE_FAILURES.has(body.code)) {
    return body.code as LookupFailure;
  }
  const message = body?.error ?? "";
  if (RE_TIMED_OUT.test(message)) {
    return "timeout";
  }
  const upstream = RE_UPSTREAM_STATUS.exec(message);
  if (upstream) {
    return failureForStatus(Number(upstream[1]));
  }
  return status >= 500 ? "unavailable" : "not-found";
}

/** Resolve one identifier through `/api/cite`. Throws only when `signal` aborts. */
export async function citeLookup(
  input: string,
  signal?: AbortSignal
): Promise<Lookup<CiteEntry>> {
  let response: Response;
  try {
    response = await fetch("/api/cite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // `inText` sends the request past the route's bibliography-only cache, whose
      // hits return the formatted text without the parsed entry these tools need.
      body: JSON.stringify({ input, style: "apa", inText: true }),
      signal,
    });
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    return { ok: false, reason: "network" };
  }
  const body = (await response
    .json()
    .catch(() => null)) as CiteResponseBody | null;
  if (response.ok && body?.entry) {
    return { ok: true, value: body.entry };
  }
  return { ok: false, reason: citeFailure(response.status, body) };
}

/* ---------------------------------------------------------------- Crossref */

const CROSSREF_WORKS = "https://api.crossref.org/works";

/**
 * Crossref asks tools to say who they are. Requests that carry a `mailto` go to its
 * "polite" pool, which allows three list queries a second where an anonymous caller
 * gets one. It names CitationEasy, never the reader.
 */
const CROSSREF_MAILTO = "support@citationeasy.com";

/** The only fields the update check reads, so a batch of twenty stays a few kilobytes. */
const UPDATE_FIELDS = [
  "DOI",
  "title",
  "container-title",
  "issued",
  "type",
  "updated-by",
  "update-to",
].join(",");

/** DOIs per list query. Twenty keeps the address short and a failure small. */
export const CROSSREF_BATCH_SIZE = 20;

export interface CrossrefDate {
  "date-parts"?: (number | null)[][];
}

/**
 * One notice linking two works. On a work, `updated-by` lists the notices that update
 * it; on a notice, `update-to` lists the works it updates. `source` is `publisher` for
 * the publisher's own deposit and `retraction-watch` for the Retraction Watch database.
 */
export interface CrossrefUpdate {
  DOI?: string;
  type?: string;
  label?: string;
  source?: string;
  updated?: CrossrefDate;
  /** The Retraction Watch record number. */
  "record-id"?: string;
}

export interface CrossrefWork {
  DOI?: string;
  type?: string;
  title?: string[];
  "container-title"?: string[];
  issued?: CrossrefDate;
  "updated-by"?: CrossrefUpdate[];
  "update-to"?: CrossrefUpdate[];
}

/** What a Crossref HTTP status means for the reader. */
function crossrefFailure(status: number): LookupFailure {
  if (status === 404) {
    return "not-found";
  }
  if (status === 429) {
    return "rate-limited";
  }
  return "unavailable";
}

/** One DOI from Crossref. A 404 means Crossref did not register it — not that it is fake. */
export async function crossrefLookup(
  doi: string,
  signal?: AbortSignal
): Promise<Lookup<CrossrefWork>> {
  let response: Response;
  try {
    response = await fetch(
      `${CROSSREF_WORKS}/${encodeURIComponent(doi)}?mailto=${CROSSREF_MAILTO}`,
      { signal }
    );
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    return { ok: false, reason: "network" };
  }
  if (!response.ok) {
    return { ok: false, reason: crossrefFailure(response.status) };
  }
  const body = (await response.json().catch(() => null)) as {
    message?: CrossrefWork;
  } | null;
  return body?.message
    ? { ok: true, value: body.message }
    : { ok: false, reason: "unavailable" };
}

/**
 * One list query for several DOIs: `filter=doi:A,doi:B`, trimmed to `UPDATE_FIELDS`.
 * A comma separates filters, so a DOI containing one cannot go in the list.
 */
export function crossrefBatchUrl(dois: readonly string[]): string {
  const filter = dois.map((doi) => `doi:${doi}`).join(",");
  return `${CROSSREF_WORKS}?filter=${encodeURIComponent(filter)}&select=${UPDATE_FIELDS}&rows=${dois.length}&mailto=${CROSSREF_MAILTO}`;
}

async function fetchWorksBatch(
  dois: readonly string[],
  signal?: AbortSignal
): Promise<Lookup<CrossrefWork[]>> {
  let response: Response;
  try {
    response = await fetch(crossrefBatchUrl(dois), { signal });
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    return { ok: false, reason: "network" };
  }
  if (!response.ok) {
    return { ok: false, reason: crossrefFailure(response.status) };
  }
  const body = (await response.json().catch(() => null)) as {
    message?: { items?: CrossrefWork[] };
  } | null;
  const items = body?.message?.items;
  return items
    ? { ok: true, value: items }
    : { ok: false, reason: "unavailable" };
}

const RETRY_AFTER_MS: Partial<Record<LookupFailure, number>> = {
  "rate-limited": 2000,
  unavailable: 1000,
  network: 1000,
};

/** One retry, after a pause, for the failures that are usually momentary. */
async function withRetry<T>(
  attempt: () => Promise<Lookup<T>>,
  signal?: AbortSignal
): Promise<Lookup<T>> {
  const first = await attempt();
  const wait = first.ok ? undefined : RETRY_AFTER_MS[first.reason];
  if (wait === undefined) {
    return first;
  }
  await pause(wait, signal);
  return attempt();
}

/**
 * Crossref's records for a set of DOIs, keyed by lower-case DOI (DOIs are
 * case-insensitive and Crossref returns them lower-cased). A DOI missing from the map
 * is one Crossref does not hold.
 */
export async function crossrefWorksByDoi(
  dois: readonly string[],
  signal?: AbortSignal
): Promise<Lookup<Map<string, CrossrefWork>>> {
  const works = new Map<string, CrossrefWork>();
  const listable = dois.filter((doi) => !doi.includes(","));
  if (listable.length > 0) {
    const batch = await withRetry(
      () => fetchWorksBatch(listable, signal),
      signal
    );
    if (!batch.ok) {
      return batch;
    }
    for (const work of batch.value) {
      if (work.DOI) {
        works.set(work.DOI.toLowerCase(), work);
      }
    }
  }
  for (const doi of dois.filter((value) => value.includes(","))) {
    const single = await withRetry(() => crossrefLookup(doi, signal), signal);
    if (single.ok) {
      works.set(doi.toLowerCase(), single.value);
    } else if (single.reason !== "not-found") {
      return single;
    }
  }
  return { ok: true, value: works };
}

/* ------------------------------------------------------------------ runner */

/** Wait `ms` — or stop at once, with the abort reason, when `signal` aborts. */
export function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", stop);
      resolve();
    }, ms);
    function stop() {
      clearTimeout(timer);
      reject(signal?.reason);
    }
    signal?.addEventListener("abort", stop, { once: true });
  });
}

/**
 * Answer one key at a time from lookups made a batch at a time.
 *
 * `lookupEach` walks the rows in order and awaits each one. The first row of a batch
 * fetches the whole batch; the rest of that batch is answered from the same response.
 * Batches start at least `spacingMs` apart, so a long list never arrives as a burst.
 * `fetchBatch` must key its map by lower-case key; a key missing from the map is
 * reported as not found.
 */
export function createBatchedLookup<T>(
  keys: readonly string[],
  fetchBatch: (
    batch: readonly string[],
    signal?: AbortSignal
  ) => Promise<Lookup<Map<string, T>>>,
  { size, spacingMs }: { size: number; spacingMs: number }
): (key: string, signal?: AbortSignal) => Promise<Lookup<T>> {
  const batchOf = new Map<string, number>();
  const batches: string[][] = [];
  for (const key of keys) {
    const id = key.toLowerCase();
    if (batchOf.has(id)) {
      continue;
    }
    const last = batches.at(-1);
    if (last && last.length < size) {
      last.push(key);
    } else {
      batches.push([key]);
    }
    batchOf.set(id, batches.length - 1);
  }

  const started = new Map<number, Promise<Lookup<Map<string, T>>>>();
  let lastStart = Number.NEGATIVE_INFINITY;
  async function load(index: number, signal?: AbortSignal) {
    const wait = lastStart + spacingMs - Date.now();
    if (wait > 0) {
      await pause(wait, signal);
    }
    lastStart = Date.now();
    return fetchBatch(batches[index], signal);
  }

  return async (key, signal) => {
    const index = batchOf.get(key.toLowerCase());
    if (index === undefined) {
      return { ok: false, reason: "not-found" };
    }
    let batch = started.get(index);
    if (!batch) {
      batch = load(index, signal);
      started.set(index, batch);
    }
    const result = await batch;
    if (!result.ok) {
      return result;
    }
    const value = result.value.get(key.toLowerCase());
    return value === undefined
      ? { ok: false, reason: "not-found" }
      : { ok: true, value };
  };
}

/**
 * Run `worker` over `items` one at a time, reporting progress after each. Returns null
 * if `signal` aborts part-way, so a superseded run never overwrites a newer one.
 */
export async function lookupEach<T, R>(
  items: readonly T[],
  worker: (item: T, index: number) => Promise<R>,
  onProgress: (done: number) => void,
  signal: AbortSignal
): Promise<R[] | null> {
  const results: R[] = [];
  try {
    for (const [index, item] of items.entries()) {
      if (signal.aborted) {
        return null;
      }
      results.push(await worker(item, index));
      onProgress(index + 1);
    }
  } catch (error) {
    if (signal.aborted) {
      return null;
    }
    throw error;
  }
  return signal.aborted ? null : results;
}

/* ------------------------------------------------------------ journal list */

/** Where `scripts/build-journal-abbreviations.mjs` writes the NLM list. */
export const JOURNAL_LIST_URL = "/data/journal-abbreviations.txt";

let journalList: Promise<JournalList> | null = null;

/**
 * The NLM journal list, fetched once per page load and kept in memory. A failed
 * fetch is forgotten, so the next call tries again.
 */
export function loadJournalList(): Promise<JournalList> {
  if (!journalList) {
    journalList = fetch(JOURNAL_LIST_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Journal list returned ${response.status}`);
        }
        return response.text();
      })
      .then(parseJournalAsset)
      .catch((error: unknown) => {
        journalList = null;
        throw error;
      });
  }
  return journalList;
}
